import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import { resolveRoleHolder } from "../lib/automation";
import type { RoleDef, RoleMapping, Workspace } from "../lib/types";
import * as work from "../lib/workClient";

/* Roles are fully customizable: create any number (gated by the plan's role
 * limit — buy more slots), rename them to your team's language, and map each to
 * your CRM, database, or relationship graph so the holder is read from the
 * source. A role can point at an account field, a contact/person by relationship
 * role, or a user referenced from a related custom object. */

// A mapping option surfaced in the picker, with its encoded <select> value.
interface MapOption { value: string; label: string; group: string; }
const encodeMapping = (m?: RoleMapping): string => {
  switch (m?.type) {
    case "account_field": return `field:${m.field}`;
    case "contact_role": return `contact:${m.contactRole}`;
    case "record_ref": return `record:${m.object}:${m.matchField}:${m.holderField}`;
    default: return "manual";
  }
};
const decodeMapping = (v: string): RoleMapping => {
  if (v.startsWith("field:")) return { type: "account_field", field: v.slice(6) };
  if (v.startsWith("contact:")) return { type: "contact_role", contactRole: v.slice(8) };
  if (v.startsWith("record:")) { const [, object, matchField, holderField] = v.split(":"); return { type: "record_ref", object, matchField, holderField }; }
  return { type: "manual" };
};

export default function Roles() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [roles, setRoles] = useState<RoleDef[]>(ws.roles);
  const [composing, setComposing] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => { work.loadRoles(ws).then((r) => { setMode(r.mode); setRoles(r.roles); }); }, [ws]);

  // Everything a role can map to, grouped for the picker.
  const mapOptions = useMemo<MapOption[]>(() => {
    const acct = ws.objects.find((o) => o.apiName === "account");
    const acctFields: MapOption[] = [
      { value: "field:ownerId", label: "CRM account owner (Salesforce / HubSpot)", group: "Account field" },
      ...(acct?.fields ?? []).filter((f) => f.type === "reference" || f.type === "text").map((f) => ({ value: `field:${f.key}`, label: f.label, group: "Account field" })),
    ];
    const contactObj = ws.objects.find((o) => o.apiName === "contact");
    const contactRoles: MapOption[] = (contactObj?.fields.find((f) => f.key === "role")?.options ?? ["Champion", "Economic Buyer", "User", "Detractor"])
      .map((r) => ({ value: `contact:${r}`, label: r, group: "Contact by relationship role" }));
    const recordRefs: MapOption[] = [];
    for (const o of ws.objects) {
      if (o.apiName === "account") continue;
      const matchField = o.fields.find((f) => f.type === "reference" && f.refObject === "account");
      if (!matchField) continue;
      for (const f of o.fields) if (f.type === "reference" && f.refObject === "user") {
        recordRefs.push({ value: `record:${o.apiName}:${matchField.key}:${f.key}`, label: `${o.label} · ${f.label}`, group: "Custom object reference" });
      }
    }
    return [...acctFields, ...contactRoles, ...recordRefs];
  }, [ws.objects]);

  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(""), 2800); };

  const patch = async (key: string, p: Partial<RoleDef>) => {
    setRoles((prev) => prev.map((r) => (r.key === key ? { ...r, ...p } : r)));
    await work.updateRole(mode, ws, key, p);
  };
  const remove = async (r: RoleDef) => {
    setRoles((prev) => prev.filter((x) => x.key !== r.key));
    await work.deleteRole(mode, ws, r.key);
  };
  const create = async (input: work.RoleInput) => {
    const res = await work.createRole(mode, ws, input);
    if (!res.ok) { flash("Couldn't create that role. Please try again."); return; }
    setRoles((prev) => [...prev, res.role]);
    setComposing(false);
  };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Roles</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Your team's language, mapped to your data</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>
            Not everyone says "CSM." Define the roles your team actually uses, then map each to a source —
            a CRM field, a contact on the account, or a user on a related record — so the right person is
            filled in automatically. Tasks and automations assign to these.
          </p>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <ModePill mode={mode} />
          <button className="btn btn--primary btn--sm" onClick={() => setComposing((v) => !v)} title="Create a role">
            <Icon name="plus" size={15} /> New role
          </button>
        </div>
      </div>

      {toast && <div className="toast"><Icon name="check" size={14} /> {toast}</div>}

      {composing && <Composer mapOptions={mapOptions} onCancel={() => setComposing(false)} onCreate={create} />}

      <div style={{ display: "grid", gap: 12 }}>
        {roles.length === 0 ? <Empty>No roles yet. Create your first one.</Empty> : roles.map((r) => (
          <RoleCard key={r.key} ws={ws} role={r} mapOptions={mapOptions} onPatch={(p) => patch(r.key, p)} onDelete={() => remove(r)} />
        ))}
      </div>
    </div>
  );
}

function RoleCard({ ws, role, mapOptions, onPatch, onDelete }: {
  ws: Workspace; role: RoleDef; mapOptions: MapOption[];
  onPatch: (p: Partial<RoleDef>) => void; onDelete: () => void;
}) {
  const [editingName, setEditingName] = useState(false);
  const isManual = !role.mapping || role.mapping.type === "manual";

  // Resolve holders across the portfolio to prove the mapping works. Uses a ws
  // snapshot carrying this card's (possibly edited) mapping.
  const resolved = useMemo(() => {
    if (isManual) return null;
    const wsE: Workspace = { ...ws, roles: ws.roles.map((r) => (r.key === role.key ? role : r)) };
    const holders = ws.accounts.map((a) => resolveRoleHolder(wsE, a.id, role.key));
    const hit = holders.filter((h) => h.kind !== "none");
    const kind = role.mapping?.type === "contact_role" ? "contact" : "person";
    const names = [...new Set(hit.map((h) => (h.kind === "user" || h.kind === "contact" ? h.name : "")).filter(Boolean))];
    return { count: hit.length, total: ws.accounts.length, sample: names.slice(0, 3), kind };
  }, [ws, role, isManual]);

  const mapSummary = mappingSummary(role.mapping, mapOptions);

  return (
    <div className="card">
      <div className="card__bd">
        <div className="row between" style={{ alignItems: "flex-start" }}>
          <div className="row" style={{ gap: 11 }}>
            <span className="n-ic" style={{ width: 34, height: 34, borderRadius: 9, display: "grid", placeItems: "center", background: "var(--surface-2)", border: "1px solid var(--line)" }}>
              <Icon name={role.scope === "relationship" ? "link" : "user"} size={16} className="faint" />
            </span>
            <div>
              {editingName ? (
                <input className="inp inp--title" autoFocus defaultValue={role.label} style={{ fontSize: 15 }}
                  onBlur={(e) => { setEditingName(false); if (e.target.value.trim() && e.target.value !== role.label) onPatch({ label: e.target.value.trim() }); }}
                  onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
              ) : (
                <b style={{ fontSize: 14.5, cursor: "text" }} onClick={() => setEditingName(true)} title="Click to rename">{role.label}</b>
              )}
              <small className="faint" style={{ display: "block", fontSize: 12.5 }}>{role.description || (role.scope === "relationship" ? "Relationship role" : "Account role")}</small>
            </div>
          </div>
          <div className="row" style={{ gap: 8 }}>
            {role.system && <span className="tag">Starter</span>}
            <button className="icon-btn" title="Delete role" onClick={onDelete}><Icon name="dot" size={16} /></button>
          </div>
        </div>

        <div className="role-map">
          <label className="labeled" style={{ flex: 1 }}>
            <span className="labeled__l">Who holds it — mapped from</span>
            <select className="inp" value={encodeMapping(role.mapping)} onChange={(e) => onPatch({ mapping: decodeMapping(e.target.value) })}>
              <option value="manual">Manual — set per account</option>
              <MapGroups options={mapOptions} />
            </select>
          </label>
          <label className="labeled" style={{ width: 150 }}>
            <span className="labeled__l">Scope</span>
            <select className="inp" value={role.scope} onChange={(e) => onPatch({ scope: e.target.value as RoleDef["scope"] })}>
              <option value="account">Account</option>
              <option value="relationship">Relationship</option>
            </select>
          </label>
        </div>

        <div className="role-resolve">
          {isManual ? (
            <><Icon name="user" size={13} className="faint" /> Set manually per account in each account's team.</>
          ) : resolved && resolved.count > 0 ? (
            <><Icon name={mapSummary.icon} size={13} className="faint" /> {mapSummary.label} — resolves to a {resolved.kind} on <b>{resolved.count}/{resolved.total}</b> accounts
              <span className="faint"> · {resolved.sample.join(", ")}{resolved.count > resolved.sample.length ? "…" : ""}</span></>
          ) : (
            <><Icon name={mapSummary.icon} size={13} className="faint" /> {mapSummary.label} — no accounts resolve yet (sync the source or check the mapping).</>
          )}
        </div>
      </div>
    </div>
  );
}

function MapGroups({ options }: { options: MapOption[] }) {
  const groups = [...new Set(options.map((o) => o.group))];
  return <>{groups.map((g) => (
    <optgroup key={g} label={g}>{options.filter((o) => o.group === g).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</optgroup>
  ))}</>;
}

function mappingSummary(m: RoleMapping | undefined, options: MapOption[]): { label: string; icon: string } {
  if (!m || m.type === "manual") return { label: "Manual", icon: "user" };
  if (m.type === "account_field") return { label: options.find((o) => o.value === `field:${m.field}`)?.label ?? `Account field ${m.field}`, icon: "plug" };
  if (m.type === "contact_role") return { label: `Contact where role is ${m.contactRole}`, icon: "link" };
  return { label: options.find((o) => o.value === `record:${m.object}:${m.matchField}:${m.holderField}`)?.label ?? `${m.object} · ${m.holderField}`, icon: "database" };
}

function Composer({ mapOptions, onCancel, onCreate }: {
  mapOptions: MapOption[]; onCancel: () => void; onCreate: (input: work.RoleInput) => void;
}) {
  const [label, setLabel] = useState("");
  const [scope, setScope] = useState<RoleDef["scope"]>("account");
  const [map, setMap] = useState("manual");
  const [description, setDescription] = useState("");

  return (
    <div className="card composer">
      <div className="card__bd">
        <input className="inp inp--title" placeholder="Role name — e.g. Implementation Consultant" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
        <div className="composer__grid" style={{ gridTemplateColumns: "1fr 1fr 180px", marginTop: 12 }}>
          <Labeled label="Who holds it — mapped from">
            <select className="inp" value={map} onChange={(e) => setMap(e.target.value)}>
              <option value="manual">Manual — set per account</option>
              <MapGroups options={mapOptions} />
            </select>
          </Labeled>
          <Labeled label="Description">
            <input className="inp" placeholder="What this role does" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Labeled>
          <Labeled label="Scope">
            <select className="inp" value={scope} onChange={(e) => setScope(e.target.value as RoleDef["scope"])}>
              <option value="account">Account</option>
              <option value="relationship">Relationship</option>
            </select>
          </Labeled>
        </div>
        <div className="row" style={{ gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button className="btn btn--sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn--primary btn--sm" disabled={!label.trim()}
            onClick={() => onCreate({ label: label.trim(), scope, description: description.trim() || undefined, mapping: decodeMapping(map) })}>
            Create role
          </button>
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="labeled"><span className="labeled__l">{label}</span>{children}</label>;
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}>
    <i />{mode === "api" ? "Live API" : "Local demo"}
  </span>;
}
